import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { LuDownload, LuPencil, LuCopy, LuTrash2, LuPlus, LuFiles, LuRefreshCw } from 'react-icons/lu';
import api from '../../services/api';
import ModalShell from '../ui/ModalShell';
import PresupuestoPropio, { generarPDF, fmt } from './PresupuestoPropio';
import JuntarPresupuestos from './JuntarPresupuestos';

// Presupuestos guardados de un cliente propio del técnico (12-oct-2026): volver a
// bajar el PDF, marcar cómo salió, editar, duplicar o borrar.
const ESTADOS = [
    { id: 'ENVIADO',   label: 'Enviado',   on: 'bg-chip text-ink ring-2 ring-[color:var(--etapa-marcha)]' },
    { id: 'ACEPTADO',  label: 'Aceptado',  on: 'text-white bg-[color:var(--etapa-listo)]' },
    { id: 'RECHAZADO', label: 'Rechazado', on: 'text-white bg-brand-red' },
];
const fechaCorta = f => (f ? String(f).slice(0, 10).split('-').reverse().join('/') : '');

// Fila compacta (13-oct-2026): antes eran 3 botones grandes de estado + una fila de acciones.
// Ahora: N°, fecha, detalle y total; abajo el estado como un chip que se cambia tocándolo y las
// acciones como íconos chicos (bajar PDF, editar, duplicar, borrar).
const SIGUIENTE = { ENVIADO: 'ACEPTADO', ACEPTADO: 'RECHAZADO', RECHAZADO: 'ENVIADO' };
const CHIP = {
    ENVIADO: 'bg-chip text-secondary',
    ACEPTADO: 'text-white bg-[color:var(--etapa-listo)]',
    RECHAZADO: 'text-white bg-brand-red',
};

function Fila({ p, cliente, onEstado, onEditar, onDuplicar, onBorrar }) {
    const [confirmar, setConfirmar] = useState(false);
    const resumen = (p.items || []).map(it => it.desc).join(' · ');
    const estado = p.estado || 'ENVIADO';
    const icono = 'w-9 h-9 rounded-xl bg-chip text-ink flex items-center justify-center active:scale-90 shrink-0';
    return (
        <div className="px-3.5 py-3 rounded-2xl bg-panel space-y-2">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <p className="text-body font-black text-ink">N° {p.numero} <span className="text-caption font-bold text-muted">· {fechaCorta(p.fecha)}</span></p>
                    <p className="text-caption text-secondary truncate">{resumen}</p>
                </div>
                <p className="text-body-lg font-black text-ink shrink-0">{fmt(p.total)}</p>
            </div>
            {confirmar ? (
                <div className="flex items-center gap-2">
                    <p className="flex-1 text-caption font-bold text-ink">¿Borrar el N° {p.numero}?</p>
                    <button onClick={() => setConfirmar(false)} className="h-9 px-3 rounded-xl bg-chip text-secondary text-label font-black">No</button>
                    <button onClick={() => onBorrar(p)} className="h-9 px-3 rounded-xl bg-brand-red text-white text-label font-black">Sí, borrar</button>
                </div>
            ) : (
                <div className="flex items-center gap-1.5">
                    <button onClick={() => onEstado(p, SIGUIENTE[estado])} title="Tocá para cambiar: Enviado → Aceptado → Rechazado"
                        className={`h-8 px-3 rounded-full text-label font-black inline-flex items-center gap-1 active:scale-95 ${CHIP[estado]}`}>
                        {ESTADOS.find(e => e.id === estado)?.label || estado} <LuRefreshCw size={11} className="opacity-70" />
                    </button>
                    <span className="flex-1" />
                    <button onClick={() => generarPDF(p, cliente)} aria-label="Bajar PDF" title="Bajar PDF" className={`${icono} !bg-brand-red !text-white`}><LuDownload size={15} /></button>
                    <button onClick={() => onEditar(p)} aria-label="Editar" title="Editar" className={icono}><LuPencil size={14} /></button>
                    <button onClick={() => onDuplicar(p)} aria-label="Duplicar" title="Duplicar" className={icono}><LuCopy size={14} /></button>
                    <button onClick={() => setConfirmar(true)} aria-label="Borrar" title="Borrar" className={`${icono} text-brand-red`}><LuTrash2 size={14} /></button>
                </div>
            )}
        </div>
    );
}

export default function PresupuestosCliente({ cliente, onCerrar, onCambio }) {
    const [lista, setLista] = useState(null);
    const [editor, setEditor] = useState(null); // null | { inicial }
    const [juntando, setJuntando] = useState(false);

    const cargar = () => api.get(`/mis-clientes/${cliente.id}/presupuestos`)
        .then(r => setLista(Array.isArray(r.data) ? r.data : []))
        .catch(() => { setLista([]); toast.error('No se pudieron cargar los presupuestos'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { cargar(); }, [cliente.id]);

    const cambiarEstado = async (p, estado) => {
        setLista(l => l.map(x => x.id === p.id ? { ...x, estado } : x));
        try { await api.put(`/mis-presupuestos/${p.id}`, { estado }); }
        catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo cambiar'); cargar(); }
    };
    const borrar = async (p) => {
        try {
            await api.delete(`/mis-presupuestos/${p.id}`);
            setLista(l => l.filter(x => x.id !== p.id)); onCambio && onCambio();
        } catch (e) { toast.error(e?.response?.data?.mensaje || 'No se pudo borrar'); }
    };
    const aceptados = (lista || []).filter(p => p.estado === 'ACEPTADO').reduce((s, p) => s + Number(p.total || 0), 0);

    return (
        <>
            <ModalShell titulo={`Presupuestos · ${cliente.nombre}`}
                subtitulo={lista && lista.length > 0 ? `${lista.length} guardado${lista.length !== 1 ? 's' : ''}${aceptados ? ` · aceptados ${fmt(aceptados)}` : ''}` : undefined}
                onCerrar={onCerrar}
                pie={
                    <button onClick={() => setEditor({ inicial: null })}
                        className="w-full h-11 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95">
                        <LuPlus size={15} /> Nuevo presupuesto
                    </button>
                }>
                {lista === null ? (
                    <div className="space-y-2">{[1, 2].map(i => <div key={i} className="h-28 rounded-2xl bg-panel animate-pulse" />)}</div>
                ) : lista.length === 0 ? (
                    <p className="py-8 text-center text-body text-muted">Todavía no le hiciste presupuestos a {cliente.nombre}.</p>
                ) : (
                    <div className="space-y-2.5">
                        {lista.length > 1 && (
                            <button type="button" onClick={() => setJuntando(true)}
                                className="w-full h-10 rounded-xl bg-chip text-ink text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-[0.99]">
                                <LuFiles size={15} /> Juntar en un solo PDF
                            </button>
                        )}
                        {lista.map(p => (
                            <Fila key={p.id} p={p} cliente={cliente} onEstado={cambiarEstado} onBorrar={borrar}
                                onEditar={x => setEditor({ inicial: x })}
                                onDuplicar={x => setEditor({ inicial: { ...x, id: undefined, numero: undefined } })} />
                        ))}
                    </div>
                )}
            </ModalShell>
            {juntando && <JuntarPresupuestos cliente={cliente} onCerrar={() => setJuntando(false)} />}
            {editor && (
                <PresupuestoPropio cliente={cliente} inicial={editor.inicial} onCerrar={() => setEditor(null)}
                    onGuardado={() => { setEditor(null); cargar(); onCambio && onCambio(); }} />
            )}
        </>
    );
}
